var CancellationToken = require('./CancellationToken');

/**
 * IframeWorkerPool can create a pool of iframes that can do tasks.
 * The purpose is to offload the main UI thread by doing heavy tasks
 * in iframes instead. It also gives much better memory management and
 * multicore CPU utilazation
 */
class IframeWorkerPool {

    /**
     * Constructor
     * @param {string} iframeUrl
     * @param {number} maxParallellTasks
     * @param {profiling} profiling
     * @param {string} profileName
     */
    constructor(iframeUrl, maxParallellTasks, profiling, profileName) {
        this.maxParallell = maxParallellTasks; //Set this to control how many paralell image resize to perform
        this._jobs = [];
        this.activeCount = 0;
        this.callbackPool = [];
        this.count = 0;
        this.poolId = this._makeId(12);
        this.profiling = profiling;
        this.profileName = profileName;
        this.baseIframeUrl = iframeUrl;

        //Attach event listener
        var self = this;
        window.addEventListener("message", function(event) {
            self._recieveIframeMessage(event);
        }.bind(self), false);

        //Initialize iframes
        this.allIframes = [];
        this.idleIframes = [];
        this.busyIframes = [];
        this.totalCreated = 0;
        this.readyStates = [];
        this.readyStatePollingTimeout = 50;
        this.initHasBeenRun = true;
    };

    /**
     * Execute Job
     * @param {string} id
     * @param {Object} paramsToIframe you want to send to the iframe
     * @return {Promise} Gives you a promise which returns the data from the iframe when it's done with it's job
     */
    executeJob(id, paramsToIframe) {
        var self = this;
        var cancelToken = new CancellationToken("iframeWorker");
        var promise = new Promise(function(resolve, reject) {
            self._jobs.push({
                jobId: id,
                params: paramsToIframe,
                resolve: resolve,
                reject: reject,
                cancelToken: cancelToken
            });
            self._processQueue();
        });
        return {
            cancelToken: cancelToken,
            promise: promise
        };
    };

    /**
     * Create a random string with given length
     * @param {number} size
     * @return {string}
     */
    _makeId(size) {
        var text = "";
        var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

        for (var i = 0; i < size; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }

        return text;
    };

    /**
     * Whether another job can be executed
     * @return {boolean}
     */
    _canExecute() {
        // Every time we probe whether we can execute, it means there's a task to be run.
        // We check whether it's necessary and possible to create a new worker for that task
        if (this.idleIframes.length === 0 && this.totalCreated < this.maxParallell - 1) {
            // Create one more
            var newIframe = document.createElement("iframe");
            newIframe.style.width = 0;
            newIframe.style.height = 0;
            newIframe.style.border = 0;
            newIframe.name = this.poolId + "_" + this.totalCreated;
            document.body.appendChild(newIframe);
            newIframe.src = this.baseIframeUrl + '?poolId=' + this.poolId + '&index=' + this.totalCreated;
            const frameIndex = this.totalCreated;
            this.allIframes[frameIndex] = newIframe;
            this.totalCreated++;
        }
        return this.idleIframes.length > 0 &&
            this.initHasBeenRun;
    };

    /**
     * Execute a job using an iframe
     * @param  {string} jobId
     * @param  {Object} params
     * @param  {Function} callback
     */
    _executeUsingIframe(jobId, params, callback) {
        this.profiling.start(this.profileName, jobId);
        const iframe = this.idleIframes.shift();
        const frameIndex = this.allIframes.indexOf(iframe);
        this.callbackPool[jobId] = {
            params: params,
            callback: callback,
            executor: frameIndex
        };
        this.busyIframes.push(iframe);
        iframe.contentWindow.postMessage({
            callbackId: jobId,
            params: params
        }, '*');
    };

    /**
     * This is a listener for messages from the iframes
     *
     * There are two types of messages received, one is
     * to signal that the iframe has loaded its page and is
     * ready for tasks. The other is to give the
     * result of the job executed
     *
     * @param {{data: { type: string, poolId: string, frameId: string, callbackId: string}}} event
     */
    _recieveIframeMessage(event) {
        if (event.data.hasOwnProperty("type") &&
            event.data.hasOwnProperty("poolId") &&
            event.data.hasOwnProperty("frameId")) {
    
            const poolId = event.data["poolId"];
            if (poolId !== this.poolId) {
                return;
            }
            const eventType = event.data["type"];
            const frameIndex = parseInt(event.data["frameId"],10);
            switch (eventType) {
                case 'iframeReady':
                    this.readyStates[frameIndex] = true;
                    const frame = this.allIframes[frameIndex];
                    this.idleIframes.push(frame);
                    this._processQueue();
                    break;
                case 'error':
                    if (!event.data.callbackId) {
                        throw "IframeWorkerPool._recieveIframeMessage() received an invalid payload on error.";
                    }

                    var data = this.callbackPool[event.data.callbackId];
                    if (data) {
                        data['callback'](null, event.data.error);
                        this._releaseBusyFrame(data.executor);
                    }
                    this.callbackPool[event.data.callbackId] = null;
                    this._processQueue();
                    break;
                case 'jobExecuted':
                    if (!event.data.callbackId) {
                        throw "IframeWorkerPool._recieveIframeMessage() received an invalid payload (missing callbackId).";
                    }
                    var data = this.callbackPool[event.data.callbackId];
                    if (data) {
                        //Run the callback function for this image so that the calling script can get its resized image
                        data['callback'](event.data.result);
                        this._releaseBusyFrame(data.executor);
                    }
                    this.callbackPool[event.data.callbackId] = null;
                    this.profiling.end(this.profileName, event.data.callbackId);
                    this._processQueue();
                    break;
                default:
                    debugger;
            }
        }
    };

    /**
     * Move a specific busy frame back to idle pool
     * @private
     * @param {number} masterIndex
     */
    _releaseBusyFrame(masterIndex) {
        const frame = this.allIframes[masterIndex];
        const busyIndex = this.busyIframes.indexOf(frame);
        if (busyIndex === -1) return;
        this.busyIframes.splice(busyIndex,1);
        this.idleIframes.push(frame);
    };

    /**
     * Processes the next queued resize job
     * @private
     */
    _processQueue() {
        var self = this;
        if (this._canExecute() && this._jobs.length > 0) {
            var job = this._jobs.shift();

            try {
                job.cancelToken.throwIfCanceled();
            } catch (e) {
                job.reject('cancelled');
            }

            self._executeUsingIframe(job.jobId, job.params, function(result,
                error) {
                if (error) {
                    var errorMessage = (error && error.message ? error.message : error);
                    job.reject(errorMessage);
                }
                else {
                    self.count++;
                    job.resolve(result);
                }
            });
        }
    };
}
module.exports = IframeWorkerPool;
